import { getIframeSessionToken, getSessionToken } from './account'
import { store } from '../store'
import { bindActionToPromise } from '../actions/utils'
import authActions from '../actions/auth'
import EnvHandler from '../utils/env-handler'

export const getHeaders = async (
  requiresAuth,
  headers = {},
  skipAutoContentType = false,
  useIframeToken = false
) => {
  const config = { Accept: 'application/json', ...headers }

  // The browser takes care of setting this header in some cases automatically,
  // like when submitting a form
  if (!skipAutoContentType) {
    config['Content-Type'] = 'application/json'
  }

  if (requiresAuth) {
    config.Authorization = `Bearer ${
      useIframeToken ? await getIframeSessionToken() : await getSessionToken()
    }`
  }

  for (const header in headers) {
    if (
      Object.prototype.hasOwnProperty.call(config, header) &&
      config[header] === false
    ) {
      // Remove header values that have been set explicitly to false
      delete config[header]
    }
  }

  return new Headers(config)
}

const buildQueryString = (params = {}) => {
  const keyNames = params ? Object.keys(params) : []
  if (keyNames.length === 0) {
    return ''
  }

  const keys = []
  for (let i = 0; i < keyNames.length; i++) {
    const keyName = keyNames[i]
    const value = params[keyName]
    if (typeof value === 'undefined') {
      continue
    }
    keys.push(`${keyName}=${value}`)
  }

  if (keys.length === 0) {
    return ''
  }

  return `?${keys.join('&')}`
}

let cachedInFrame = false
let cachedEnv = null
const request = async (url, settings = {}) => {
  let env

  if (cachedEnv) {
    env = cachedEnv
  } else {
    const environment = await EnvHandler.get()
    env = environment
    // eslint-disable-next-line require-atomic-updates
    cachedEnv = env
  }

  const {
    requiresAuth = true,
    method = 'GET',
    body = false,
    headers,
    query,
    skipAutoContentType,
    useIframeToken,
    timeout,
    ...rest
  } = settings
  if (typeof useIframeToken === 'boolean') {
    cachedInFrame = useIframeToken
  }

  let abortController, signal
  if (timeout) {
    abortController = new AbortController()
    signal = abortController.signal
  }

  const options = {
    headers: await getHeaders(
      requiresAuth,
      headers,
      skipAutoContentType,
      cachedInFrame
    ),
    method,
    signal,
    ...rest,
  }
  if (body) {
    options.body = body
  }

  try {
    const queryString = buildQueryString(query)
    let to
    if (timeout) {
      to = setTimeout(() => abortController.abort(), timeout)
    }
    const response = await fetch(env + url + queryString, options)
    if (to) {
      clearTimeout(to)
    }
    return response
  } catch (e) {
    // Network error
    if (e instanceof TypeError) {
      e.message =
        `There was a network problem${
          e.statusCode ? ` (${e.statusCode})` : ''
        }. ` + 'Try refreshing the page.'
    }
    throw e
  }
}

const attemptRefresh = async (response, ...requestProps) => {
  if (!store || !store.dispatch) return

  // Attempt to refresh the session, but only if this isn't already an
  // attempt to refresh the session :P
  // In that case, just log the user out, there's nothing more we can do here
  if (/\/auth\/refresh$/i.test(response.url)) {
    store.dispatch(authActions.logout.request())
    return
  }

  // Attempt to refresh the token; if it fails for whatever reason (i.e. there is
  // no token) then just log the user out
  try {
    await bindActionToPromise(store.dispatch, authActions.refresh.request)()
  } catch (e) {
    store.dispatch(authActions.logout.request())
    return
  }

  return await request(...requestProps)
}

// Retry a request - if it throws an error, it'll throw just like `throwOnHTTPError`
// but otherwise if we get a 503 again, throw a network unavailable error.
// const retry = async (...requestProps) => {
//   const response = await request(...requestProps)
//   if (response.status === 503) throw new Error('Network unavailable (503)')
//   return response
// }

const throwOnHTTPError = async (origResponse, ...requestProps) => {
  const status = origResponse.status
  if (status < 400) {
    return origResponse
  }
  // If we get a 401 Unauthorized response from the server, try to refresh our
  // session token, but only if this is not an attempt to actually log in.
  // A 401 Unauthorized in that scenario means the account credentials are incorrect
  // and we need to display that error message to the user.

  // Actually, just log out
  if (!/\/auth\/login$/i.test(origResponse.url) && status === 401) {
    // const response = await attemptRefresh(origResponse, ...requestProps)
    // if (response) return response
    store.dispatch(authActions.logout.request())
    const invalidCredentialsErrors = new Error('Invalid credentials')
    throw invalidCredentialsErrors
  }

  // Duck out early if the network is unavailable - avoids a JSON parsing error below
  if ([502, 503].includes(status)) {
    // return await retry(...requestProps)
    const networkError = new Error(`Network unavailable (${status})`)
    throw networkError
  }

  let error = new Error()
  try {
    error = await origResponse.json()
    error.statusCode = status

    if (status === 500) {
      error.message = `There was a problem (500): ${error.message}`
    }
  } catch (e) {
    // If for some reason the API returns malformed JSON, show that I guess?
    error.message = `There was a problem: ${e.message}`
  }

  error.isApiError = true

  throw error
}

export const req = async (url, settings = {}) => {
  const { parseJSON = true, noThrow = false, ...rest } = settings
  const orig = await request(url, rest)

  // HTTP 204 - No content: just return early
  if (orig.status === 204) {
    return {}
  }

  // HTTP Error codes
  if (noThrow) {
    if (parseJSON) {
      return await orig.json()
    } else {
      return orig
    }
  }

  const response = await throwOnHTTPError(orig, url, rest)

  if (parseJSON) {
    return await response.json()
  } else {
    return response
  }
}

export default req
